import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users,
  Search,
  Download,
  Trash2,
  Eye,
  TrendingUp,
  Activity,
  Clock,
  Award,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser } from '../../utils/supabase/client';
import { getSessions } from '../../utils/supabase/queries';
import { isAutoGradable } from '../../utils/grading/autoGradeConfig';
import {
  getSessionStudents,
  getStudentActivityGrades,
  removeStudentFromSession,
  exportToCSV,
  type GradeSummary
} from '../../utils/supabase/analytics';

export default function Students() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<GradeSummary[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'score' | 'completion'>('name');
  const [selectedStudent, setSelectedStudent] = useState<GradeSummary | null>(null);
  const [studentGrades, setStudentGrades] = useState<any[]>([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalRecords / pageSize);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      loadStudents();
    }
  }, [selectedSession, searchQuery, sortBy, currentPage]);

  const loadSessions = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in');
        setLoading(false);
        return;
      }

      const sessionsData = await getSessions(user.id);
      setSessions(sessionsData);
      
      if (sessionsData.length > 0 && !selectedSession) {
        setSelectedSession(sessionsData[0].id);
      } else if (sessionsData.length === 0) {
        // No sessions available (e.g. shared lessons were deleted by owner)
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    if (!selectedSession) return;

    setLoading(true);
    try {
      const pagination = { page: currentPage, pageSize };
      const { data, total } = await getSessionStudents(
        selectedSession,
        searchQuery,
        sortBy,
        pagination
      );
      
      setStudents(data);
      setTotalRecords(total);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = async (student: GradeSummary) => {
    setSelectedStudent(student);
    
    try {
      const grades = await getStudentActivityGrades(student.session_id, student.student_name);
      setStudentGrades(grades);
    } catch (error) {
      console.error('Error loading student grades:', error);
      toast.error('Failed to load student details');
    }
  };

  const handleRemoveStudent = async (student: GradeSummary) => {
    if (!confirm(`Remove ${student.student_name} from this session?`)) {
      return;
    }

    try {
      await removeStudentFromSession(student.session_id, student.student_name);
      toast.success('Student removed');
      loadStudents();
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error removing student:', error);
      toast.error('Failed to remove student');
    }
  };

  const handleExport = () => {
    if (students.length === 0) {
      toast.error('No students to export');
      return;
    }

    const exportData = students.map(s => ({
      student_name: s.student_name,
      total_score: s.total_score,
      percentage: s.percentage,
      completion_rate: s.completion_rate,
      completed_activities: s.completed_activities,
      total_activities: s.total_activities,
      last_activity: s.last_activity_at || 'N/A'
    }));

    exportToCSV(exportData, 'students-list');
    toast.success('Students exported');
  };

  return (
    <div className="flex h-full bg-background">
      {/* Main Content */}
      <div className={`flex-1 flex flex-col ${selectedStudent ? 'hidden lg:flex' : 'flex'}`}>
        {/* Header */}
        <header className="border-b border-border bg-card px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Students</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage and view student performance
              </p>
            </div>
            <Button
              onClick={handleExport}
              className="bg-accent hover:bg-accent/90 w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Export List
            </Button>
          </div>
        </header>

        {/* Filters */}
        <div className="border-b border-border bg-card px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            <Select value={selectedSession} onValueChange={setSelectedSession}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue placeholder="Select Session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map(session => (
                  <SelectItem key={session.id} value={session.id}>
                    {session.session_code} - {new Date(session.created_at).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative w-full md:flex-1">
              <Input
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10"
              />
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            </div>

            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="completion">Completion</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Students List */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No students found</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border sticky top-0">
                    <tr>
                      <th className="text-left p-4 font-semibold">Student Name</th>
                      <th className="text-center p-4 font-semibold">Score</th>
                      <th className="text-center p-4 font-semibold">Completion</th>
                      <th className="text-center p-4 font-semibold">Activities</th>
                      <th className="text-left p-4 font-semibold">Last Active</th>
                      <th className="text-center p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          <div className="font-medium">{student.student_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {student.total_score.toFixed(0)} / {student.max_possible_score.toFixed(0)} pts
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`font-semibold ${
                            student.percentage >= 80 ? 'text-green-600' :
                            student.percentage >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {student.percentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary"
                                style={{ width: `${student.completion_rate}%` }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {student.completion_rate.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-center text-sm">
                          {student.completed_activities} / {student.total_activities}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {student.last_activity_at 
                            ? new Date(student.last_activity_at).toLocaleString()
                            : 'N/A'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewStudent(student)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveStudent(student)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {students.map((student) => (
                  <div key={student.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{student.student_name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {student.total_score.toFixed(0)} / {student.max_possible_score.toFixed(0)} pts
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewStudent(student)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveStudent(student)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Score</p>
                        <p className={`font-semibold ${
                          student.percentage >= 80 ? 'text-green-600' :
                          student.percentage >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {student.percentage.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Completion</p>
                        <p className="font-semibold">{student.completion_rate.toFixed(0)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Activities</p>
                        <p className="font-semibold">
                          {student.completed_activities} / {student.total_activities}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Last Active</p>
                        <p className="text-xs">
                          {student.last_activity_at 
                            ? new Date(student.last_activity_at).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalRecords > pageSize && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Student Detail Panel */}
      {selectedStudent && (
        <div className={`${selectedStudent ? 'flex' : 'hidden'} lg:flex w-full lg:w-96 border-l border-border bg-card flex-col`}>
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold">Student Details</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedStudent(null)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold mb-1">{selectedStudent.student_name}</h3>
                <p className="text-sm text-muted-foreground">Performance Overview</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background p-3 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Score</span>
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedStudent.percentage >= 80 ? 'text-green-600' :
                    selectedStudent.percentage >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {selectedStudent.percentage.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-background p-3 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Completion</span>
                  </div>
                  <p className="text-2xl font-bold">{selectedStudent.completion_rate.toFixed(0)}%</p>
                </div>
                <div className="bg-background p-3 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Activities</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {selectedStudent.completed_activities}/{selectedStudent.total_activities}
                  </p>
                </div>
                <div className="bg-background p-3 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Last Active</span>
                  </div>
                  <p className="text-xs font-medium">
                    {selectedStudent.last_activity_at 
                      ? new Date(selectedStudent.last_activity_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Activity Grades */}
              <div>
                <h4 className="font-semibold mb-3">Activity Scores</h4>
                {studentGrades.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No activity scores yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {studentGrades.map((grade: any, idx) => {
                      const slide = grade.slides;
                      const activityTitle = slide?.content?.title || `Activity ${idx + 1}`;
                      const autoGraded = isAutoGradable(slide?.type);
                      const maxScore = slide?.content?.points || slide?.content?.maxPoints || 10; // Match Gradebook logic
                      const scorePercentage = grade.score ? (grade.score / maxScore) * 100 : 0;
                      
                      return (
                        <div key={grade.id} className="flex items-center justify-between p-3 bg-background rounded-xl">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{activityTitle}</p>
                            <p className="text-xs text-muted-foreground">
                              {autoGraded ? 'Auto-graded' : grade.graded ? 'Manually graded' : 'Pending'}
                            </p>
                          </div>
                          <div className="text-right">
                            {grade.score !== null ? (
                              <>
                                <p className={`font-semibold ${
                                  scorePercentage >= 70 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {grade.score} / {maxScore}
                                </p>
                                <Badge variant={scorePercentage >= 70 ? 'default' : 'destructive'} className="text-xs">
                                  {Math.round(scorePercentage)}%
                                </Badge>
                              </>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                Not Graded
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button
                variant="destructive"
                className="w-full"
                onClick={() => handleRemoveStudent(selectedStudent)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove from Session
              </Button>
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
